import React, { useState } from 'react';
import useContainerWidth from './useContainerWidth';
import ChartTooltip from './ChartTooltip';
import { fmtDay, fmtInt } from '../format';

/**
 * Stacked per-day columns. `series`: [{ key, label, values, color }] with one
 * value per entry of `days`. `overlay` (optional) is drawn as a thin marker
 * above each column — used for failures so they stay visible on busy days.
 */
const DailyBars = ({ days = [], series = [], overlay, height = 170 }) => {
  const [containerRef, measuredWidth] = useContainerWidth(600);
  const [hover, setHover] = useState(null);
  const w = Math.max(measuredWidth, 120);
  const padL = 34, padR = 8, padT = 12, padB = 22;
  const W = w - padL - padR;
  const H = height - padT - padB;
  const n = days.length || 1;

  const totals = days.map((_, i) => series.reduce((s, ser) => s + (ser.values[i] || 0), 0));
  const maxV = Math.max(1, ...totals, ...(overlay ? overlay.values : []));
  const slot = W / n;
  const barW = Math.max(2, Math.min(28, slot * 0.66));
  const y = (v) => padT + H - (v / maxV) * H;
  const empty = totals.every((t) => t === 0) && (!overlay || overlay.values.every((v) => !v));

  // Label every k-th day so labels never collide at 90 days on a narrow card.
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(W / 56))));
  const ticks = [0, Math.round(maxV / 2), maxV];

  return (
    <div ref={containerRef} className="qa-chart">
      <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={w - padR} y1={y(t)} y2={y(t)} className="qa-chart__grid" />
            <text x={padL - 6} y={y(t) + 3.5} textAnchor="end" className="qa-chart__tick">{fmtInt(t)}</text>
          </g>
        ))}
        {days.map((day, i) => {
          const x = padL + slot * i + (slot - barW) / 2;
          let acc = 0;
          return (
            <g
              key={day}
              onMouseEnter={() => setHover({ i, x: padL + slot * i + slot / 2 })}
            >
              <rect x={padL + slot * i} y={padT} width={slot} height={H} fill="transparent" />
              {series.map((ser) => {
                const v = ser.values[i] || 0;
                if (!v) return null;
                const top = y(acc + v);
                const bottom = y(acc);
                acc += v;
                return (
                  <rect key={ser.key} x={x} y={top} width={barW} height={Math.max(1, bottom - top)}
                    fill={ser.color} rx={2} opacity={hover && hover.i !== i ? 0.55 : 1} />
                );
              })}
              {overlay && overlay.values[i] > 0 && (
                <rect x={x} y={y(totals[i]) - 4} width={barW} height={2.5} fill={overlay.color} rx={1} />
              )}
              {i % labelEvery === 0 && (
                <text x={padL + slot * i + slot / 2} y={height - 6} textAnchor="middle" className="qa-chart__tick">
                  {fmtDay(day)}
                </text>
              )}
            </g>
          );
        })}
        {empty && (
          <text x={padL + W / 2} y={padT + H / 2} textAnchor="middle" className="qa-chart__empty">No activity in this window</text>
        )}
      </svg>
      {hover && (
        <ChartTooltip
          x={Math.min(hover.x, w - 170)}
          y={8}
          title={fmtDay(days[hover.i])}
          rows={[
            ...series.filter((s) => s.values[hover.i]).map((s) => ({ label: s.label, value: fmtInt(s.values[hover.i]), color: s.color })),
            ...(overlay && overlay.values[hover.i] ? [{ label: overlay.label, value: fmtInt(overlay.values[hover.i]), color: overlay.color }] : []),
            ...(totals[hover.i] ? [] : [{ label: 'Nothing', value: '' }]),
          ]}
        />
      )}
      <div className="qa-chart__legend">
        {series.map((s) => (
          <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>
        ))}
        {overlay && <span><i style={{ background: overlay.color, height: 3 }} />{overlay.label}</span>}
      </div>
    </div>
  );
};

export default DailyBars;
